/**
 * TemplatePickerModal Component
 * Modal for browsing platform templates and creating custom templates.
 * Supports adding platform templates to the user's collection and creating
 * custom templates with a built-in editor. The onTemplateAdded callback
 * fires for both paths, allowing consuming pages to link the template to a project.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FileText,
  X,
  Loader2,
  AlertTriangle,
  Check,
  Sparkles,
  UserPlus,
  Plus,
  ArrowLeft,
  Save,
  Target,
  LayoutDashboard,
  GitBranch,
  ClipboardList,
  Flag,
} from 'lucide-react';
import { toast } from 'sonner';
import { useTemplates } from '@/hooks/useTemplates';
import { useAssignments } from '@/hooks/useAssignments';
import { useProjectConfig } from '@/hooks/useProjectConfig';
import { templatesApi } from '@/services/templatesApi';
import { errorMessages } from '@/lib/errors';
import type { DefaultTemplateMetadata, TemplateMetadata } from '@/services/templatesApi';

// Icon mapping for platform templates
const ICON_MAP: Record<string, React.ElementType> = {
  Target,
  Flag,
  LayoutDashboard,
  GitBranch,
  FileText,
  ClipboardList,
  Layers: GitBranch,
  BookOpen: FileText,
};

// Color classes helper
const getColorClasses = (color: string) => {
  const colors: Record<string, { bg: string; text: string }> = {
    blue: { bg: 'bg-blue-100', text: 'text-blue-600' },
    emerald: { bg: 'bg-emerald-100', text: 'text-emerald-600' },
    purple: { bg: 'bg-purple-100', text: 'text-purple-600' },
    amber: { bg: 'bg-amber-100', text: 'text-amber-600' },
    rose: { bg: 'bg-rose-100', text: 'text-rose-600' },
    indigo: { bg: 'bg-indigo-100', text: 'text-indigo-600' },
    slate: { bg: 'bg-slate-100', text: 'text-slate-600' },
  };
  return colors[color] || colors.slate;
};

// Color options for custom template editor
const COLOR_OPTIONS = [
  { value: 'blue', label: 'Blue' },
  { value: 'emerald', label: 'Green' },
  { value: 'purple', label: 'Purple' },
  { value: 'amber', label: 'Amber' },
  { value: 'rose', label: 'Rose' },
  { value: 'indigo', label: 'Indigo' },
  { value: 'slate', label: 'Grey' },
];

// Output format options
const OUTPUT_FORMATS = [
  { value: 'custom', label: 'Custom' },
  { value: 'general', label: 'General' },
  { value: 'adr', label: 'ADR' },
  { value: 'position-statement', label: 'Position Statement' },
  { value: 'requirements', label: 'Requirements' },
];

interface TemplatePickerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onTemplateAdded?: (template: TemplateMetadata) => void;
  /** When provided, new templates are atomically assigned to this project via TransactWriteItems */
  projectId?: string;
}

export const TemplatePickerModal: React.FC<TemplatePickerModalProps> = ({
  isOpen,
  onClose,
  onTemplateAdded,
  projectId,
}) => {
  const { t } = useTranslation();
  const { templates: existingTemplates, createTemplate } = useTemplates();
  const { createAndAssign } = useAssignments();
  const { projects } = useProjectConfig();

  // Check if the target project already has a template with the given name
  const projectHasTemplateName = useCallback((templateName: string): boolean => {
    if (!projectId) return false;
    const project = projects.find(p => p.id === projectId);
    if (!project) return false;
    const assignedIds = project.templateIds || [];
    return existingTemplates.some(
      tmpl => assignedIds.includes(tmpl.id) && tmpl.name.toLowerCase() === templateName.toLowerCase()
    );
  }, [projectId, projects, existingTemplates]);

  // View state: 'browse' (platform templates) or 'create' (custom editor)
  const [view, setView] = useState<'browse' | 'create'>('browse');

  // Platform templates state
  const [platformTemplates, setPlatformTemplates] = useState<DefaultTemplateMetadata[]>([]);
  const [platformLoading, setPlatformLoading] = useState(false);
  const [platformError, setPlatformError] = useState<string | null>(null);
  const [addingTemplateId, setAddingTemplateId] = useState<string | null>(null);
  const [addedTemplateIds, setAddedTemplateIds] = useState<Set<string>>(new Set());

  // Custom template editor state
  const [editorName, setEditorName] = useState('');
  const [editorDescription, setEditorDescription] = useState('');
  const [editorContent, setEditorContent] = useState('# Template Title\n\n## Section 1\n\nYour content here...\n');
  const [editorFormat, setEditorFormat] = useState('custom');
  const [editorColor, setEditorColor] = useState('slate');
  const [editorSaving, setEditorSaving] = useState(false);

  // Track whether we've already fetched to avoid re-fetching when API returns 0 templates
  const hasFetchedRef = useRef(false);

  // Fetch platform templates when modal opens
  useEffect(() => {
    if (isOpen && !hasFetchedRef.current && !platformLoading) {
      hasFetchedRef.current = true;
      const fetchPlatform = async () => {
        setPlatformLoading(true);
        setPlatformError(null);
        try {
          const templates = await templatesApi.listDefaultTemplates();
          setPlatformTemplates(templates);
        } catch (err) {
          setPlatformError(errorMessages.loadTemplates(err));
        } finally {
          setPlatformLoading(false);
        }
      };
      fetchPlatform();
    }
  }, [isOpen, platformLoading]);

  // Reset custom editor state
  const resetEditor = useCallback(() => {
    setEditorName('');
    setEditorDescription('');
    setEditorContent('# Template Title\n\n## Section 1\n\nYour content here...\n');
    setEditorFormat('custom');
    setEditorColor('slate');
  }, []);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setView('browse');
      resetEditor();
      hasFetchedRef.current = false;
      setPlatformTemplates([]);
      setAddedTemplateIds(new Set());
    }
  }, [isOpen, resetEditor]);

  // Save custom template: always creates original; if projectId, also creates a copy assigned to the project
  const handleSaveCustomTemplate = useCallback(async () => {
    if (!editorName.trim()) return;

    // Check for duplicate template name in user's templates
    const trimmedName = editorName.trim().toLowerCase();
    const duplicate = existingTemplates.find(tmpl => tmpl.name.toLowerCase() === trimmedName);
    if (duplicate) {
      toast.error(t('templates.templateNameExists', 'A template with this name already exists'));
      return;
    }

    // Check for duplicate template name in the target project
    if (projectId && projectHasTemplateName(editorName.trim())) {
      const projectName = projects.find(p => p.id === projectId)?.projectName || '';
      toast.error(t('templates.templateAlreadyInProject', { templateName: editorName.trim(), projectName }));
      return;
    }

    setEditorSaving(true);
    try {
      const request = {
        name: editorName.trim(),
        description: editorDescription.trim(),
        content: editorContent,
        outputFormat: editorFormat,
        color: editorColor,
      };

      // Always create the original template (appears in Custom Templates section)
      await createTemplate(request);

      let assigned;
      if (projectId) {
        // Also create a copy assigned to the project
        assigned = await createAndAssign(projectId, request);
        onTemplateAdded?.(assigned);
      }

      toast.success(t('templatePicker.templateCreated', { name: editorName.trim() }));
      resetEditor();
      setView('browse');
    } catch (err) {
      toast.error(errorMessages.saveTemplate(err));
    } finally {
      setEditorSaving(false);
    }
  }, [editorName, editorDescription, editorContent, editorFormat, editorColor, existingTemplates, createTemplate, createAndAssign, projectId, projects, projectHasTemplateName, onTemplateAdded, resetEditor, t]);

  // Add platform template to user's collection (atomic create+assign when projectId is available)
  const handleAddPlatformTemplate = async (template: DefaultTemplateMetadata) => {
    // Check for duplicate template name in the target project
    if (projectId && projectHasTemplateName(template.templateName)) {
      const projectName = projects.find(p => p.id === projectId)?.projectName || '';
      toast.error(t('templates.templateAlreadyInProject', { templateName: template.templateName, projectName }));
      return;
    }
    setAddingTemplateId(template.templateId);
    try {
      const fullTemplate = await templatesApi.getDefaultTemplate(template.templateId);
      const request = {
        name: template.templateName,
        description: template.description,
        content: fullTemplate.templateStructure || '',
        outputFormat: template.outputFormat || 'custom',
        color: template.color || 'slate',
        prompt: fullTemplate.prompt,
      };

      let created;
      if (projectId) {
        // Atomic: create template + assign to project in one transaction
        created = await createAndAssign(projectId, request);
      } else {
        created = await createTemplate(request);
      }

      setAddedTemplateIds(prev => new Set(prev).add(template.templateId));
      onTemplateAdded?.(created);
      toast.success(t('templatePicker.templateAdded', { name: template.templateName }));
    } catch (err) {
      toast.error(errorMessages.addTemplate(err));
    } finally {
      setAddingTemplateId(null);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[85vh] flex flex-col animate-in fade-in zoom-in duration-200">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-slate-200">
          <div>
            {view === 'create' ? (
              <>
                <button
                  onClick={() => { setView('browse'); resetEditor(); }}
                  className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 mb-1 transition-colors"
                >
                  <ArrowLeft className="w-3.5 h-3.5" />
                  {t('templatePicker.backToTemplates')}
                </button>
                <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
                  <Plus className="w-5 h-5 text-indigo-600" />
                  {t('templatePicker.createCustomTemplate')}
                </h2>
              </>
            ) : (
              <>
                <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-blue-600" />
                  {t('templatePicker.addDocumentType')}
                </h2>
                <p className="text-sm text-slate-500 mt-1">
                  {t('templatePicker.chooseOrCreate')}
                </p>
              </>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {view === 'browse' ? (
            <>
              {/* Create Custom button at top */}
              <button
                onClick={() => setView('create')}
                className="w-full mb-4 flex items-center gap-3 p-4 rounded-xl border-2 border-dashed border-indigo-300 hover:border-indigo-400 hover:bg-indigo-50/50 transition-all group"
              >
                <div className="p-2 rounded-lg bg-indigo-100 shrink-0">
                  <Plus className="w-5 h-5 text-indigo-600" />
                </div>
                <div className="text-left">
                  <h4 className="font-semibold text-indigo-700 group-hover:text-indigo-800">{t('templatePicker.createCustomTemplate')}</h4>
                  <p className="text-sm text-slate-500">{t('templatePicker.designFromScratch')}</p>
                </div>
              </button>

              {/* Divider */}
              {!platformLoading && platformTemplates.length > 0 && (
                <div className="flex items-center gap-3 mb-4">
                  <div className="flex-1 border-t border-slate-200" />
                  <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">{t('templatePicker.platformTemplates')}</span>
                  <div className="flex-1 border-t border-slate-200" />
                </div>
              )}

              {/* Error banner */}
              {platformError && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
                  <p className="text-red-700 text-sm">{platformError}</p>
                </div>
              )}

              {/* Loading state */}
              {platformLoading && (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-blue-600 mr-3" />
                  <span className="text-slate-600">{t('templatePicker.loadingPlatformTemplates')}</span>
                </div>
              )}

              {/* Platform template list */}
              {!platformLoading && platformTemplates.length > 0 && (
                <div className="space-y-2">
                  {platformTemplates.map(template => {
                    const colorClasses = getColorClasses(template.color || 'slate');
                    const IconComponent = ICON_MAP[template.icon || ''] || FileText;
                    const isAdding = addingTemplateId === template.templateId;
                    const isAdded = addedTemplateIds.has(template.templateId);

                    return (
                      <div
                        key={template.templateId}
                        className="flex items-center gap-3 p-4 rounded-xl border-2 border-slate-200 hover:border-blue-300 transition-all"
                      >
                        <div className={`p-2 rounded-lg ${colorClasses.bg} shrink-0`}>
                          <IconComponent className={`w-5 h-5 ${colorClasses.text}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-semibold text-slate-900 truncate">{template.templateName}</h4>
                          <p className="text-sm text-slate-500 truncate">{template.description}</p>
                        </div>
                        <button
                          onClick={() => handleAddPlatformTemplate(template)}
                          disabled={isAdding || isAdded}
                          className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg font-medium text-sm transition-colors shrink-0 ${
                            isAdded
                              ? 'bg-green-100 text-green-700 cursor-default'
                              : isAdding
                                ? 'bg-blue-100 text-blue-400 cursor-wait'
                                : 'bg-blue-600 hover:bg-blue-700 text-white'
                          }`}
                        >
                          {isAdded ? (
                            <><Check className="w-4 h-4" /> {t('templatePicker.added')}</>
                          ) : isAdding ? (
                            <><Loader2 className="w-4 h-4 animate-spin" /> {t('templatePicker.adding')}</>
                          ) : (
                            <><UserPlus className="w-4 h-4" /> {t('templatePicker.add')}</>
                          )}
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          ) : (
            /* Custom template editor */
            <div className="space-y-4">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">{t('templatePicker.templateNameLabel')}</label>
                <input
                  type="text"
                  value={editorName}
                  onChange={(e) => setEditorName(e.target.value)}
                  placeholder={t('templatePicker.templateNamePlaceholder')}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                  autoFocus
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">{t('templatePicker.descriptionLabel')}</label>
                <input
                  type="text"
                  value={editorDescription}
                  onChange={(e) => setEditorDescription(e.target.value)}
                  placeholder={t('templatePicker.descriptionPlaceholder')}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                />
              </div>

              {/* Color & Format row */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">{t('templatePicker.colorLabel')}</label>
                  <div className="flex gap-2 flex-wrap">
                    {COLOR_OPTIONS.map(opt => {
                      const cls = getColorClasses(opt.value);
                      return (
                        <button
                          key={opt.value}
                          onClick={() => setEditorColor(opt.value)}
                          className={`w-8 h-8 rounded-full border-2 transition-all ${cls.bg} ${
                            editorColor === opt.value ? 'border-slate-900 scale-110' : 'border-transparent hover:border-slate-300'
                          }`}
                          title={opt.label}
                        />
                      );
                    })}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">{t('templatePicker.outputFormatLabel')}</label>
                  <select
                    value={editorFormat}
                    onChange={(e) => setEditorFormat(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                  >
                    {OUTPUT_FORMATS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Template content */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">{t('templatePicker.templateStructureLabel')}</label>
                <textarea
                  value={editorContent}
                  onChange={(e) => setEditorContent(e.target.value)}
                  rows={10}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none font-mono text-sm"
                  placeholder={t('templatePicker.templateStructurePlaceholder')}
                />
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-slate-200 bg-slate-50">
          {view === 'create' ? (
            <>
              <button
                onClick={() => { setView('browse'); resetEditor(); }}
                className="px-4 py-2 text-slate-700 hover:bg-slate-200 rounded-lg transition-colors font-medium"
              >
                {t('templatePicker.cancel')}
              </button>
              <button
                onClick={handleSaveCustomTemplate}
                disabled={!editorName.trim() || editorSaving}
                className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {editorSaving ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> {t('templatePicker.saving')}</>
                ) : (
                  <><Save className="w-4 h-4" /> {t('templatePicker.createTemplate')}</>
                )}
              </button>
            </>
          ) : (
            <button
              onClick={onClose}
              className="px-4 py-2 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300 transition-colors font-medium"
            >
              {t('templatePicker.done')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default TemplatePickerModal;
